<template>
  <div class="space-y-6">
    <p class="text-body text-gray-600 dark:text-gray-300">
      Add the cities, towns, or regions where you provide services. This helps customers in those areas find you.
    </p>

    <!-- Search input - uses Google's PlaceAutocompleteElement -->
    <div>
      <label class="form-label">Add Service Area</label>
      <div ref="autocompleteContainer" class="place-autocomplete-wrapper" />
      <p class="form-helper">
        Start typing to search for cities, towns, or regions
      </p>
    </div>

    <!-- Service areas list -->
    <div v-if="wizard.serviceAreas.value.length > 0">
      <h4 class="form-label mb-3">
        Your Service Areas ({{ wizard.serviceAreas.value.length }})
      </h4>
      <div class="space-y-2">
        <div
          v-for="area in wizard.serviceAreas.value"
          :key="area.id"
          class="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3">
          <div class="flex items-center gap-3">
            <svg class="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <div>
              <p class="font-medium text-gray-900 dark:text-gray-100">
                {{ area.display_name || area.name }}
              </p>
              <p v-if="area.formatted_address && area.formatted_address !== area.name" class="text-sm text-gray-500 dark:text-gray-400">
                {{ area.formatted_address }}
              </p>
            </div>
            <span
              v-if="area.primary"
              class="badge badge-primary ml-2">
              Primary
            </span>
          </div>

          <button
            type="button"
            class="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400"
            @click="handleDeleteArea(area.id)">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
      <p class="mt-4 text-body text-gray-600 dark:text-gray-300">
        No service areas added yet
      </p>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Search for and add at least one area to continue
      </p>
    </div>

    <!-- Tips -->
    <div class="rounded-lg p-4" style="background-color: var(--color-info-50);">
      <div class="flex">
        <svg class="h-5 w-5 flex-shrink-0" style="color: var(--color-info-500);" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
        </svg>
        <div class="ml-3">
          <h4 class="text-sm font-medium" style="color: var(--color-info-700);">
            Tip
          </h4>
          <p class="mt-1 text-sm" style="color: var(--color-info-600);">
            Adding more specific service areas (like cities or neighborhoods) helps customers in those areas find you more easily. You can add multiple areas.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { loadGoogleMaps } from '~/javascript/utils/google_maps'

const props = defineProps({
  wizard: { type: Object, required: true },
  mapKey: { type: String, required: true }
})

const autocompleteContainer = ref(null)
let placeAutocomplete = null
let google = null

async function initAutocomplete() {
  if (!props.mapKey) {
    console.warn('Google Maps API key not provided')
    return
  }

  // Wait for DOM to be ready
  await nextTick()

  if (!autocompleteContainer.value) {
    console.warn('Autocomplete container not found')
    return
  }

  // Don't reinitialize if already done
  if (placeAutocomplete) {
    return
  }

  try {
    google = await loadGoogleMaps(props.mapKey)
    const { PlaceAutocompleteElement } = await google.maps.importLibrary('places')

    // Create the new PlaceAutocompleteElement (it creates its own input)
    placeAutocomplete = new PlaceAutocompleteElement({
      types: ['(regions)'] // Cities, neighborhoods, postal codes, etc.
    })

    // Append to container
    autocompleteContainer.value.appendChild(placeAutocomplete)

    // Listen for place selection using the new API - event is 'gmp-select'
    placeAutocomplete.addEventListener('gmp-select', handlePlaceSelect)
  } catch (err) {
    console.error('Failed to load Google Maps:', err)
  }
}

onMounted(() => {
  initAutocomplete()
})

onUnmounted(() => {
  if (placeAutocomplete && google) {
    placeAutocomplete.removeEventListener('gmp-select', handlePlaceSelect)
    // Remove the element from DOM
    if (placeAutocomplete.parentNode) {
      placeAutocomplete.parentNode.removeChild(placeAutocomplete)
    }
    placeAutocomplete = null
  }
})

async function handlePlaceSelect(event) {
  // The event contains placePrediction, which needs to be converted to a Place
  const { placePrediction } = event

  if (!placePrediction) {
    console.warn('No placePrediction in event')
    return
  }

  // Convert to Place object
  const place = placePrediction.toPlace()

  // Fetch the fields we need
  await place.fetchFields({
    fields: ['id', 'displayName', 'formattedAddress', 'location', 'addressComponents']
  })

  const placeId = place.id

  if (!placeId) {
    console.warn('No place ID found')
    return
  }

  // Check if area already exists
  const exists = props.wizard.serviceAreas.value.some((area) => area.place_id === placeId)

  if (exists) {
    alert('This area has already been added')
    // Clear the autocomplete input
    if (placeAutocomplete) {
      const input = placeAutocomplete.querySelector('input')
      if (input) input.value = ''
    }
    return
  }

  const location = place.location

  try {
    await props.wizard.addServiceArea({
      name: place.displayName,
      place_id: placeId,
      formatted_address: place.formattedAddress,
      latitude: location?.lat(),
      longitude: location?.lng(),
      primary: props.wizard.serviceAreas.value.length === 0 // First area is primary
    })

    // Clear the autocomplete input
    if (placeAutocomplete) {
      const input = placeAutocomplete.querySelector('input')
      if (input) input.value = ''
    }
  } catch (err) {
    console.error('Failed to add service area:', err)
    alert('Failed to add service area. Please try again.')
  }
}

async function handleDeleteArea(areaId) {
  if (!confirm('Are you sure you want to remove this service area?')) return

  try {
    await props.wizard.removeServiceArea(areaId)
  } catch (err) {
    console.error('Failed to delete service area:', err)
    alert('Failed to remove service area. Please try again.')
  }
}
</script>

<style>
/* Style the Google PlaceAutocompleteElement to match our form inputs */
/* Using non-scoped styles because the element is dynamically added */
.place-autocomplete-wrapper {
  width: 100%;
}

.place-autocomplete-wrapper gmp-place-autocomplete {
  display: block;
  width: 100%;
}

.place-autocomplete-wrapper input {
  width: 100%;
  padding: 0.625rem 0.75rem !important;
  font-size: 0.875rem !important;
  line-height: 1.25rem !important;
  color: #111827 !important;
  background-color: #f9fafb !important;
  border: 1px solid #d1d5db !important;
  border-radius: 0.5rem !important;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
  box-sizing: border-box !important;
}

.place-autocomplete-wrapper input:focus {
  outline: none !important;
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
}

.place-autocomplete-wrapper input::placeholder {
  color: #9ca3af !important;
}
</style>
